import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StudentPickerWithCreate } from "@/components/StudentPickerWithCreate";
import { FlagButton } from "@/components/FlagButton";
import { ArrowLeft, ArrowRight, Check, FileText, FileCheck, Receipt, ShoppingCart, ClipboardCheck, Mail } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const STEPS = [
  { n: 1, label: "Review Transaction", icon: ClipboardCheck },
  { n: 2, label: "Create Sale",         icon: ShoppingCart },
  { n: 3, label: "Confirm Pro-forma",   icon: FileText },
  { n: 4, label: "Approve Invoice",     icon: FileCheck },
  { n: 5, label: "Send Receipt",        icon: Receipt },
];

const fmt = (minor, currency) => `${(currency || "MYR").toUpperCase()} ${((minor || 0) / 100).toFixed(2)}`;

export default function ActionWizard() {
  const { tid } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [events, setEvents] = useState([]);
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/action-queue/${tid}`);
    setState(data);
    return data;
  }, [tid]);

  useEffect(() => {
    (async () => {
      try {
        await refresh();
        const [cs, sk, ev, of] = await Promise.all([
          api.get("/customers"), api.get("/catalogue/skus"),
          api.get("/catalogue/events"), api.get("/catalogue/offers"),
        ]);
        setCustomers(cs.data); setSkus(sk.data); setEvents(ev.data); setOffers(of.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    })();
  }, [refresh]);

  if (!state) return <PageBody><p className="text-sm text-slate-500">Loading…</p></PageBody>;

  // Which step is the operator on right now? unmatched → step 0 (assign student first).
  const currentStep = state.status === "unmatched" ? 0 : (state.workflow_step || 1);

  const gotoStep = async (n) => {
    setBusy(true);
    try { setState(await api.post(`/action-queue/${tid}/step`, { step: n }).then((r) => r.data)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resolveAndClose = async () => {
    setBusy(true);
    try {
      await api.post(`/action-queue/${tid}/resolve`);
      toast.success("Marked resolved");
      nav("/action-required");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <PageBody className="space-y-6" data-testid="action-wizard-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/action-required")} data-testid="wizard-back-to-list">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to queue
        </Button>
        <div className="ml-auto"><FlagButton resource="transactions" id={tid} flag={state.flagged} onChanged={refresh} testId="flag-tx-button" /></div>
      </div>

      <ProgressBar currentStep={currentStep} unmatched={state.status === "unmatched"} onJump={(n) => currentStep >= n && gotoStep(n)} />

      {state.status === "unmatched" ? (
        <StepUnmatched state={state} customers={customers} onCustomersUpdate={setCustomers} onDone={refresh} />
      ) : currentStep === 1 ? (
        <Step1Review state={state} onNext={() => gotoStep(2)} busy={busy} />
      ) : currentStep === 2 ? (
        <Step2CreateSale state={state} customers={customers} skus={skus} events={events} offers={offers}
          onCustomersUpdate={setCustomers}
          onBack={() => gotoStep(1)}
          onSaleCreated={async (sale) => { await api.post(`/action-queue/${tid}/link-sale`, { sale_id: sale.id }); await refresh(); await gotoStep(3); }} />
      ) : currentStep === 3 ? (
        <Step3Proforma state={state} onBack={() => gotoStep(2)} onNext={async () => { await refresh(); await gotoStep(4); }} refresh={refresh} />
      ) : currentStep === 4 ? (
        <Step4Invoice state={state} onBack={() => gotoStep(3)} onNext={async () => { await refresh(); await gotoStep(5); }} refresh={refresh} />
      ) : currentStep === 5 ? (
        <Step5Receipt state={state} onBack={() => gotoStep(4)} onDone={resolveAndClose} refresh={refresh} />
      ) : null}
    </PageBody>
  );
}

function ProgressBar({ currentStep, unmatched, onJump }) {
  return (
    <div className="bg-white border rounded-md p-4">
      <div className="flex items-center gap-2 overflow-x-auto">
        {unmatched && (
          <div className="flex items-center gap-2 px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm font-semibold">
            <span className="w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center text-xs">!</span>
            Step 0 — Assign Student
          </div>
        )}
        {STEPS.map(({ n, label, icon: Icon }, idx) => {
          const done = !unmatched && n < currentStep;
          const active = !unmatched && n === currentStep;
          const clickable = !unmatched && n <= currentStep;
          return (
            <React.Fragment key={n}>
              <button
                onClick={() => clickable && onJump(n)}
                disabled={!clickable}
                className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium border transition-colors ${
                  done ? "bg-emerald-50 border-emerald-200 text-emerald-800 cursor-pointer hover:bg-emerald-100" :
                  active ? "bg-blue-50 border-blue-200 text-blue-800 cursor-default" :
                  "bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed"
                }`}
                data-testid={`wizard-step-${n}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  done ? "bg-emerald-600 text-white" : active ? "bg-blue-600 text-white" : "bg-slate-300 text-white"
                }`}>{done ? <Check className="w-3.5 h-3.5" /> : n}</span>
                <Icon className="w-4 h-4 hidden md:block" />
                <span className="whitespace-nowrap">{label}</span>
              </button>
              {idx < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 min-w-[8px]" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function StepUnmatched({ state, customers, onCustomersUpdate, onDone }) {
  const [studentId, setStudentId] = useState("");
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    if (!studentId) return;
    setBusy(true);
    try {
      await api.post(`/transactions/${state.id}/assign`, {
        student_id: studentId, expected_revision: state.revision || 0, reason: "Assigned via Action Required wizard",
      });
      toast.success("Student assigned");
      await onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Assign failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Assign a student</CardTitle>
        <CardDescription>This transaction was not matched automatically. Pick or create a student before starting the 5-step flow.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <TxSummary state={state} />
        <div>
          <Label>Student / Learner</Label>
          <StudentPickerWithCreate
            customers={customers} value={studentId} onChange={setStudentId}
            onCreated={(s) => onCustomersUpdate((prev) => [s, ...prev.filter((p) => p.id !== s.id)])}
            testId="wizard-assign-student"
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={assign} disabled={!studentId || busy} data-testid="wizard-assign-submit">
            {busy ? "Assigning…" : "Assign and continue"}<ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Step1Review({ state, onNext, busy }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Step 1 — Review Transaction</CardTitle>
        <CardDescription>Confirm the payment details before creating any records.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <TxSummary state={state} />
        <div className="flex justify-end">
          <Button onClick={onNext} disabled={busy} data-testid="wizard-confirm-tx">
            Confirm transaction <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Step2CreateSale({ state, customers, skus, events, offers, onCustomersUpdate, onBack, onSaleCreated }) {
  const [eventId, setEventId] = useState("");
  const [offerId, setOfferId] = useState("");
  const [skuId, setSkuId] = useState("");
  const [paymentPlan, setPaymentPlan] = useState("full");
  const [deposit, setDeposit] = useState("");
  const [instalments, setInstalments] = useState(3);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const currency = (state.currency || "MYR").toUpperCase();
  const unitPriceMinor = state.amount_minor || 0;

  // Offers filtered to the picked event (if any)
  const eventOffers = React.useMemo(() => {
    if (!eventId) return [];
    return offers.filter((o) => o.event_id === eventId);
  }, [eventId, offers]);

  // Selected offer drives allowed plans & default_instalments
  const selectedOffer = React.useMemo(
    () => offers.find((o) => o.id === offerId) || null,
    [offerId, offers]
  );
  const allowedPlans = selectedOffer
    ? (selectedOffer.allowed_plans?.length ? selectedOffer.allowed_plans : [selectedOffer.default_plan || "full"])
    : ["full", "deposit_balance", "instalment"];

  // When an offer is picked, sync sku_id and reset payment plan to the offer's default
  React.useEffect(() => {
    if (!selectedOffer) return;
    setSkuId(selectedOffer.sku_id);
    const defaultPlan = allowedPlans[0];
    setPaymentPlan(defaultPlan);
    setInstalments(selectedOffer.default_instalments || selectedOffer.instalments || 3);
  }, [offerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If already linked to a sale, offer to skip forward.
  if (state.workflow_sale_id && state.sale) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Step 2 — Sale already created</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">This transaction already has a linked sale <span className="font-mono">{state.sale.number}</span>.</p>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
            <Button onClick={() => onSaleCreated(state.sale)} data-testid="wizard-skip-sale">Continue <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const depositMinor = Math.max(0, Math.round(Number(deposit || 0) * 100));
  const balanceMinor = Math.max(0, unitPriceMinor - depositMinor);
  const nInstalments = Math.max(1, parseInt(instalments || 1));
  const instalmentMinor = Math.floor(unitPriceMinor / nInstalments);
  const instalmentRemainder = unitPriceMinor - instalmentMinor * nInstalments;

  const buildSchedule = () => {
    if (paymentPlan === "deposit_balance") {
      return [
        { index: 1, amount_minor: depositMinor, note: "Deposit" },
        { index: 2, amount_minor: balanceMinor, note: "Balance" },
      ];
    }
    if (paymentPlan === "instalment") {
      return Array.from({ length: nInstalments }, (_, i) => ({
        index: i + 1,
        // Last instalment absorbs the rounding remainder so the sum matches total.
        amount_minor: instalmentMinor + (i === nInstalments - 1 ? instalmentRemainder : 0),
      }));
    }
    return [];
  };

  const submit = async () => {
    if (!skuId) { toast.error("Pick an event offer or a package so the sale has a product line."); return; }
    if (paymentPlan === "deposit_balance" && depositMinor <= 0) {
      toast.error("Enter a deposit amount greater than zero.");
      return;
    }
    if (paymentPlan === "deposit_balance" && depositMinor >= unitPriceMinor) {
      toast.error("Deposit must be less than the total price.");
      return;
    }
    setBusy(true);
    try {
      const sku = skus.find((s) => s.id === skuId);
      const quantity = 1;
      const line_total_minor = quantity * unitPriceMinor;
      const payload = {
        buyer_id: state.student_id,
        learner_id: state.student_id,
        event_id: eventId || null,
        currency,
        payment_plan: paymentPlan,
        plan_instalments: paymentPlan === "instalment" ? nInstalments : 1,
        deposit_minor: paymentPlan === "deposit_balance" ? depositMinor : 0,
        plan_schedule: buildSchedule(),
        payment_plan_notes: notes || null,
        lines: [{
          sku_id: skuId,
          description: sku?.default_description || sku?.name || "",
          quantity,
          unit_price_minor: unitPriceMinor,
          line_total_minor,
        }],
        notes: `Auto-created from transaction ${state.transaction_id?.slice(0, 24)}…`,
      };
      const { data: sale } = await api.post("/sales", payload);
      toast.success(`Draft sale ${sale.number} created`);
      await onSaleCreated(sale);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sale creation failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Step 2 — Create Sale</CardTitle>
        <CardDescription>Pick the event, package offer and payment plan. The closer can override any plan detail.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <TxSummary state={state} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Event (optional)</Label>
            <Select value={eventId} onValueChange={(v) => { setEventId(v); setOfferId(""); }}>
              <SelectTrigger data-testid="wizard-event"><SelectValue placeholder="Select event…" /></SelectTrigger>
              <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{eventId && eventOffers.length ? "Package offer" : "Package"}</Label>
            {eventId && eventOffers.length > 0 ? (
              <Select value={offerId} onValueChange={setOfferId}>
                <SelectTrigger data-testid="wizard-offer"><SelectValue placeholder="Pick an offer…" /></SelectTrigger>
                <SelectContent>
                  {eventOffers.map((o) => {
                    const s = skus.find((x) => x.id === o.sku_id);
                    return <SelectItem key={o.id} value={o.id}>{s?.code} · {s?.name} — {fmt(o.price_minor, o.currency)}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Select value={skuId} onValueChange={setSkuId}>
                <SelectTrigger data-testid="wizard-sku"><SelectValue placeholder="Pick a package…" /></SelectTrigger>
                <SelectContent>{skus.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div>
          <Label>Payment plan</Label>
          <Select value={paymentPlan} onValueChange={setPaymentPlan}>
            <SelectTrigger data-testid="wizard-plan"><SelectValue /></SelectTrigger>
            <SelectContent>
              {allowedPlans.includes("full") && <SelectItem value="full">Full payment</SelectItem>}
              {allowedPlans.includes("deposit_balance") && <SelectItem value="deposit_balance">Deposit + Balance</SelectItem>}
              {allowedPlans.includes("instalment") && <SelectItem value="instalment">Instalment</SelectItem>}
            </SelectContent>
          </Select>
          {selectedOffer && (
            <div className="text-[11px] text-slate-500 mt-1">
              This offer allows: {allowedPlans.map((p) => (p === "full" ? "Full" : p === "deposit_balance" ? "Deposit + Balance" : "Instalment")).join(", ")}
            </div>
          )}
        </div>

        {paymentPlan === "deposit_balance" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3" data-testid="wizard-deposit-block">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Deposit amount ({currency})</Label>
                <Input type="number" step="0.01" min="0" value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="0.00" data-testid="wizard-deposit-amount" />
              </div>
              <div>
                <Label>Balance</Label>
                <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm" data-testid="wizard-balance">
                  {fmt(balanceMinor, currency)}
                </div>
              </div>
              <div>
                <Label>Total price</Label>
                <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm">
                  {fmt(unitPriceMinor, currency)}
                </div>
              </div>
            </div>
          </div>
        )}

        {paymentPlan === "instalment" && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3" data-testid="wizard-instalment-block">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Number of instalments</Label>
                <Input type="number" min="1" max="60" value={instalments} onChange={(e) => setInstalments(e.target.value)} data-testid="wizard-instalment-count" />
              </div>
              <div>
                <Label>Per instalment</Label>
                <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm" data-testid="wizard-instalment-amount">
                  {fmt(instalmentMinor, currency)}
                </div>
              </div>
              <div>
                <Label>Total price</Label>
                <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm">
                  {fmt(unitPriceMinor, currency)}
                </div>
              </div>
            </div>
            {instalmentRemainder > 0 && (
              <div className="text-[11px] text-blue-800">The final instalment absorbs the rounding difference of {fmt(instalmentRemainder, currency)}.</div>
            )}
          </div>
        )}

        {paymentPlan !== "full" && (
          <div>
            <Label>Payment arrangement notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={paymentPlan === "instalment" ? "e.g. Balance via Maybank CC instalment 6 months" : "e.g. Deposit paid today, balance by end of month"}
              rows={3}
              data-testid="wizard-plan-notes"
            />
          </div>
        )}

        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs">
          Line total will be <strong className="font-mono">{fmt(unitPriceMinor, currency)}</strong> (from the transaction amount).
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack} disabled={busy}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
          <Button onClick={submit} disabled={busy || !skuId} data-testid="wizard-create-sale">
            {busy ? "Creating…" : "Create Sale"} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Step3Proforma({ state, onBack, onNext, refresh }) {
  const [busy, setBusy] = useState(false);
  const [pf, setPf] = useState(state.proforma);

  useEffect(() => {
    // On entering this step, ensure the sale is confirmed so a pro-forma exists.
    (async () => {
      if (pf) return;
      if (!state.workflow_sale_id) return;
      try {
        // Confirming a sale auto-generates a pro-forma (per G1 workflow). If already
        // confirmed, we look up its child pro-forma from the sale's chain.
        if (state.sale?.status === "draft") {
          await api.post(`/sales/${state.workflow_sale_id}/confirm`);
        }
        // Find the pro-forma linked to this sale
        const { data: proformas } = await api.get(`/proformas?sale_id=${state.workflow_sale_id}`);
        const first = proformas?.[0];
        if (first) {
          await api.post(`/action-queue/${state.id}/link-doc`, { proforma_id: first.id });
          setPf(first);
          await refresh();
        }
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Pro-forma auto-generation failed"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmPf = async () => {
    if (!pf) return;
    setBusy(true);
    try {
      if (pf.status === "draft") await api.post(`/proformas/${pf.id}/confirm`);
      toast.success(`Pro-forma ${pf.number} confirmed`);
      await onNext();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Confirm failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Step 3 — Pro-forma</CardTitle>
        <CardDescription>Draft pro-forma auto-generated from the confirmed sale. Review and confirm.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        {pf ? (
          <DocSummary label="Pro-forma" number={pf.number} status={pf.status} currency={pf.currency} amountMinor={pf.total_minor} link={`/proformas`} />
        ) : (
          <p className="text-sm text-slate-500">Generating pro-forma…</p>
        )}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack} disabled={busy}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
          <Button onClick={confirmPf} disabled={busy || !pf} data-testid="wizard-confirm-pf">
            {busy ? "Confirming…" : "Confirm Pro-forma"} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Step4Invoice({ state, onBack, onNext, refresh }) {
  const [busy, setBusy] = useState(false);
  const [inv, setInv] = useState(state.invoice);

  useEffect(() => {
    (async () => {
      // If webhook already produced an invoice, use it. Otherwise the invoice was
      // auto-generated when the pro-forma was confirmed (see convert_proforma).
      if (inv) return;
      const iid = state.invoice_id || state.proforma?.invoice_id;
      if (iid) {
        try {
          const { data } = await api.get(`/invoices/${iid}`);
          setInv(data);
          if (!state.invoice_id) {
            await api.post(`/action-queue/${state.id}/link-doc`, { invoice_id: data.id });
            await refresh();
          }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load invoice"); }
        return;
      }
      // Fall back to searching invoices by sale_id (belt-and-braces)
      if (state.workflow_sale_id) {
        try {
          const { data: invoices } = await api.get(`/invoices?sale_id=${state.workflow_sale_id}`);
          const first = Array.isArray(invoices) ? invoices.find((i) => i.status !== "void") : null;
          if (first) {
            setInv(first);
            await api.post(`/action-queue/${state.id}/link-doc`, { invoice_id: first.id });
            await refresh();
          }
        } catch (e) { /* silent — will surface via "no invoice yet" state */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async () => {
    if (!inv) return;
    setBusy(true);
    try {
      if (inv.status === "draft" || inv.status === "pending") await api.post(`/invoices/${inv.id}/approve`);
      toast.success(`Invoice ${inv.number || inv.id.slice(0, 8)} approved`);
      await onNext();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approve failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Step 4 — Invoice</CardTitle>
        <CardDescription>Draft invoice generated from the confirmed pro-forma. Review the number and approve.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        {inv ? (
          <DocSummary label="Invoice" number={inv.number} status={inv.status} currency={inv.currency} amountMinor={inv.total_minor} link={`/invoices`} />
        ) : (
          <p className="text-sm text-slate-500">Generating invoice…</p>
        )}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack} disabled={busy}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
          <Button onClick={approve} disabled={busy || !inv} data-testid="wizard-approve-inv">
            {busy ? "Approving…" : "Approve Invoice"} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Step5Receipt({ state, onBack, onDone, refresh }) {
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(state.receipt);
  const [emailTo, setEmailTo] = useState(state.student_email || "");

  useEffect(() => {
    (async () => {
      if (receipt) { setEmailTo(receipt.remitter_email || state.student_email || ""); return; }
      // Look for a receipt already linked to this gateway transaction (auto-created by webhook flow).
      try {
        const { data: receipts } = await api.get(`/receipts?gateway_transaction_id=${state.id}`);
        const first = Array.isArray(receipts) ? receipts[0] : null;
        if (first) {
          setReceipt(first);
          setEmailTo(first.remitter_email || state.student_email || "");
          await api.post(`/action-queue/${state.id}/link-doc`, { receipt_id: first.id });
          await refresh();
        }
      } catch { /* silent — no receipt endpoint filter, may be normal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    if (!receipt) return;
    if (!emailTo.trim()) { toast.error("Enter an email to send the receipt."); return; }
    setBusy(true);
    try {
      await api.post(`/receipts/${receipt.id}/email`, { to: emailTo.trim() });
      toast.success("Receipt emailed to student");
      await onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Step 5 — Receipt</CardTitle>
        <CardDescription>Receipt linked to the transaction and invoice. Email it to the student to complete the flow.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        {receipt ? (
          <DocSummary label="Receipt" number={receipt.or_number || receipt.id.slice(0, 8)} status={receipt.status} currency={receipt.currency} amountMinor={receipt.amount_minor} link={`/receipts`} />
        ) : (
          <p className="text-sm text-slate-500">Locating receipt…</p>
        )}
        <div>
          <Label>Send to</Label>
          <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="student@example.com" data-testid="wizard-receipt-email" />
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack} disabled={busy}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onDone} disabled={busy} data-testid="wizard-skip-send">Skip &amp; Resolve</Button>
            <Button onClick={send} disabled={busy || !receipt || !emailTo.trim()} data-testid="wizard-send-receipt">
              <Mail className="w-4 h-4 mr-1.5" />{busy ? "Sending…" : "Send Receipt"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TxSummary({ state }) {
  const amount = fmt(state.amount_minor, state.currency);
  const studentName = state.student_name || "Unmatched";
  const studentEmail = state.student_email || "";
  const gateway = state.gateway || "—";
  const isTest = state.mode === "test";
  const txId = state.transaction_id || "—";
  const receivedAt = fmtDateTime(state.received_at);
  const status = (state.status || "").replace(/_/g, " ");

  return (
    <div
      className="rounded-md border border-slate-200 bg-slate-50 p-4 grid gap-x-4 gap-y-3 text-sm grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="wizard-tx-summary"
    >
      <Field label="Student">
        <div className="min-w-0">
          <div
            className={`truncate font-medium ${state.student_name ? "text-slate-900" : "text-rose-700"}`}
            title={studentName}
            data-testid="wizard-tx-student-name"
          >
            {studentName}
          </div>
          {studentEmail && (
            <div
              className="truncate text-xs text-slate-500 font-mono"
              title={studentEmail}
              data-testid="wizard-tx-student-email"
            >
              {studentEmail}
            </div>
          )}
        </div>
      </Field>

      <Field label="Amount">
        <span
          className="font-mono font-semibold tabular-nums whitespace-nowrap text-slate-900"
          data-testid="wizard-tx-amount"
        >
          {amount}
        </span>
      </Field>

      <Field label="Gateway">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="capitalize truncate" title={gateway} data-testid="wizard-tx-gateway">
            {gateway}
          </span>
          {isTest && (
            <Badge
              variant="outline"
              className="shrink-0 h-4 px-1.5 text-[9px] uppercase tracking-wider border-amber-400 bg-amber-50 text-amber-800"
              data-testid="wizard-tx-mode"
            >
              Test
            </Badge>
          )}
        </div>
      </Field>

      <Field label="Received">
        <span className="font-mono text-xs whitespace-nowrap text-slate-700" data-testid="wizard-tx-received">
          {receivedAt}
        </span>
      </Field>

      <Field label="Status">
        <Badge variant="outline" className="capitalize" data-testid="wizard-tx-status">
          {status || "—"}
        </Badge>
      </Field>

      <Field label="Transaction ID" className="sm:col-span-2 lg:col-span-1">
        <span
          className="block font-mono text-xs text-slate-700 truncate"
          title={txId}
          data-testid="wizard-tx-id"
        >
          {txId}
        </span>
      </Field>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`min-w-0 overflow-hidden ${className}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="text-sm mt-1 min-w-0">{children}</div>
    </div>
  );
}

function DocSummary({ label, number, status, currency, amountMinor, link }) {
  return (
    <div className="rounded border bg-white p-4 flex flex-wrap items-center gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-mono font-semibold text-sm">{number}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Status</div>
        <Badge variant="outline" className="uppercase">{status}</Badge>
      </div>
      <div className="ml-auto">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
        <div className="font-mono font-semibold">{fmt(amountMinor, currency)}</div>
      </div>
      {link && <Link to={link} className="text-xs text-emerald-700 hover:underline">Open in {label}s ↗</Link>}
    </div>
  );
}
